/**
 * Warehouses list — V1.0.x inventory location master.
 *
 * Design mirrors the stock summary page:
 *   • 4-tile KPI strip using Card + CardBody with left accent bar.
 *   • table-fixed with colgroup widths, warehouse icon + name.
 *   • Dot-pill status, colored type chips.
 *
 * @see apps/web/src/features/inventory/stock/stock-summary-page.tsx — design reference
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, Package, Plus, Search as SearchIcon, Warehouse } from "lucide-react";
import type { warehouses as warehousesContract } from "@flint/api-contract";
import type { z } from "zod";
import {
  Card,
  CardBody,
} from "@flint/ui";
import { ListPagination, avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { usePermission } from "../../../lib/permissions.ts";
import "./warehouses-list-page.css";

type WarehouseRow = z.infer<typeof warehousesContract.warehouseRow>;

const PAGE_SIZE = 50;

function formatRupees(paiseStr: string): string {
  const rupees = Number(BigInt(paiseStr)) / 100;
  return `₹${rupees.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function typeLabel(t: WarehouseRow["warehouseType"]): string {
  switch (t) {
    case "physical":
      return "Physical";
    case "virtual":
      return "Virtual";
    case "job_work":
      return "Job Work";
  }
}

// Type chip: colored by warehouse type
function TypeChip({ type }: { type: WarehouseRow["warehouseType"] }) {
  const styles = {
    physical: "bg-indigo-50 text-indigo-700 border-indigo-100",
    virtual: "bg-amber-50 text-amber-700 border-amber-100",
    job_work: "bg-violet-50 text-violet-700 border-violet-100",
  };
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-lg border text-xs font-semibold whitespace-nowrap ${styles[type]}`}
    >
      {typeLabel(type)}
    </span>
  );
}

// Status dot-pill
function StatusPill({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border ${
        isActive
          ? "bg-emerald-50 text-emerald-700 border-emerald-100"
          : "bg-bg text-muted border-line"
      }`}
    >
      <span
        aria-hidden
        className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? "bg-emerald-500" : "bg-line-2"}`}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

export function WarehousesListPage() {
  const canManage = usePermission("inventory.manage");
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);
  const [query, setQuery] = useState("");
  const [cursorStack, setCursorStack] = useState<string[]>([]);

  const list = trpc.warehouses.list.useQuery(
    { includeInactive: showInactive },
    { placeholderData: (prev) => prev },
  );

  const warehouses = list.data?.warehouses ?? [];

  // Client-side name/code search. Warehouses are a small, fully-loaded set
  // (the list has no server pagination — counts and paging are all derived
  // here), so searching in-memory keeps the whole page coherent and instant.
  const q = query.trim().toLowerCase();
  const filtered = q
    ? warehouses.filter(
        (w) =>
          w.name.toLowerCase().includes(q) || (w.code?.toLowerCase().includes(q) ?? false),
      )
    : warehouses;

  // Reset to page 1 when the visible result set changes.
  useEffect(() => {
    setCursorStack([]);
  }, [showInactive, q]);

  const pageNumber = cursorStack.length + 1;
  const pageStart = cursorStack.length * PAGE_SIZE;
  const pageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = pageItems.length === 0 ? 0 : pageStart + 1;
  const showingTo = pageStart + pageItems.length;
  const hasPrev = cursorStack.length > 0;
  const hasNext = pageStart + PAGE_SIZE < filtered.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => setCursorStack((s) => [...s, String(pageStart + PAGE_SIZE)]);

  // KPI tiles + header counts describe the whole dataset (not the search
  // view); the table + footer + pagination reflect the filtered set.
  const totalCount = warehouses.length;
  const activeCount = warehouses.filter((w) => w.isActive).length;
  const physicalCount = warehouses.filter((w) => w.warehouseType === "physical").length;
  const virtualCount = warehouses.filter(
    (w) => w.warehouseType === "virtual" || w.warehouseType === "job_work",
  ).length;

  return (
    <div className="warehouses-list-page px-6 pt-5 pb-16 max-w-[1320px] mx-auto w-full space-y-5">
      {/* ── Page head ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">
            Warehouses
          </h1>
          <p className="mt-1 text-sm text-muted flex items-center gap-2 flex-wrap">
            <span>
              <span className="font-mono font-semibold text-ink">{totalCount}</span> total
            </span>
            <span aria-hidden className="w-1 h-1 rounded-full bg-line-2" />
            <span>
              <span className="font-mono font-semibold text-ink">{activeCount}</span> active
            </span>
            <span aria-hidden className="w-1 h-1 rounded-full bg-line-2" />
            <span>
              <span className="font-mono font-semibold text-ink">{physicalCount}</span> physical
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg"
            onClick={() => setShowInactive((v) => !v)}
          >
            {showInactive ? "Hide inactive" : "Show inactive"}
          </button>
          {canManage && (
            <Link
              to="/inventory/warehouses/new"
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
            >
              <Plus size={13} aria-hidden />
              New warehouse
            </Link>
          )}
        </div>
      </header>

      {/* ── KPI strip ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiTile
          accent="sky"
          icon={<Warehouse size={14} strokeWidth={2} />}
          label="Total warehouses"
          value={String(totalCount)}
          meta="all locations including inactive"
        />
        <KpiTile
          accent="indigo"
          icon={<Warehouse size={14} strokeWidth={2} />}
          label="Physical"
          value={String(physicalCount)}
          meta="godowns & stores"
        />
        <KpiTile
          accent="amber"
          icon={<Package size={14} strokeWidth={2} />}
          label="Virtual"
          value={String(virtualCount)}
          meta="transit, consignment & job work"
        />
        <KpiTile
          accent="emerald"
          icon={<Warehouse size={14} strokeWidth={2} />}
          label="Active"
          value={String(activeCount)}
          meta="receiving stock movements"
        />
      </div>

      {/* ── Search ────────────────────────────────────────────────── */}
      {warehouses.length > 0 || q ? (
        <label className="flex items-center gap-2 h-9 px-3 w-full max-w-[380px] rounded-md border border-line bg-bg focus-within:border-sky-600 focus-within:bg-paper focus-within:ring-2 focus-within:ring-sky-100 transition-colors">
          <SearchIcon size={14} strokeWidth={2} aria-hidden className="text-muted shrink-0" />
          <input
            type="search"
            placeholder="Search by name or code…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 min-w-0 bg-transparent border-0 outline-0 text-sm placeholder:text-muted text-ink"
            aria-label="Search warehouses"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-xs text-muted hover:text-ink"
              aria-label="Clear search"
            >
              clear
            </button>
          ) : null}
        </label>
      ) : null}

      {/* ── Body ──────────────────────────────────────────────────── */}
      {list.isLoading ? (
        <p className="text-sm text-muted py-4">Loading warehouses…</p>
      ) : list.error ? (
        <div className="rounded-xl border border-line bg-paper p-12 text-center">
          <h3 className="font-semibold text-ink mb-1">Couldn't load warehouses</h3>
          <p className="text-sm text-muted">
            Something went wrong loading this list. Please refresh the page and try again.
          </p>
        </div>
      ) : warehouses.length === 0 ? (
        <div className="rounded-xl border border-line bg-paper p-12 text-center">
          <h3 className="font-semibold text-ink mb-1">No warehouses yet</h3>
          <p className="text-sm text-muted mb-4">
            Add your first warehouse to start tracking stock by location.
          </p>
          {canManage && (
            <Link
              to="/inventory/warehouses/new"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
            >
              <Plus size={13} aria-hidden /> New warehouse
            </Link>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-line bg-paper p-12 text-center">
          <h3 className="font-semibold text-ink mb-1">No warehouses match "{query}"</h3>
          <p className="text-sm text-muted">Try a different name or code, or clear the search.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-line bg-paper shadow-sm">
          {/* table scroll wrapper */}
          <div className="overflow-x-auto">
            <table
              aria-label="Warehouses"
              className="w-full min-w-[700px] table-fixed border-collapse text-sm"
            >
              <colgroup>
                <col style={{ width: "30%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-line bg-bg">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    Name
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    Code
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    Type
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">
                    Items stocked
                  </th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-muted">
                    Stock value
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted">
                    Status
                  </th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {pageItems.map((row) => (
                  <WarehouseTableRow
                    key={row.id}
                    row={row}
                    onOpen={() =>
                      void navigate({
                        to: "/inventory/warehouses/$warehouseId",
                        params: { warehouseId: row.id },
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* table footer */}
          <div className="border-t border-line px-4 py-2.5 flex items-center gap-4 flex-wrap text-xs text-muted bg-bg">
            <span>
              Showing{" "}
              <span className="font-mono font-semibold text-ink">{pageItems.length}</span> /{" "}
              <span className="font-mono font-semibold text-ink">{filtered.length}</span>
            </span>
            <span aria-hidden className="w-px h-3 bg-line" />
            <span>
              Physical <span className="font-mono font-semibold text-ink">{physicalCount}</span>
            </span>
            <span aria-hidden className="w-px h-3 bg-line" />
            <span>
              Virtual <span className="font-mono font-semibold text-ink">{virtualCount}</span>
            </span>
            <span aria-hidden className="w-px h-3 bg-line" />
            <span>
              Active <span className="font-mono font-semibold text-ink">{activeCount}</span>
            </span>
          </div>

          <ListPagination
            pageNumber={pageNumber}
            showingFrom={showingFrom}
            showingTo={showingTo}
            total={filtered.length}
            sortLabel="sorted by name"
            hasPrev={hasPrev}
            hasNext={hasNext}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </div>
      )}
    </div>
  );
}

// ── KPI tile — mirrors stock summary KpiTile ──────────────────────

type Accent = "sky" | "indigo" | "emerald" | "amber" | "rose" | "muted";

const ACCENT_BAR: Record<Accent, string> = {
  sky: "bg-sky-500",
  indigo: "bg-indigo-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  rose: "bg-rose-500",
  muted: "bg-line",
};
const ACCENT_ICON: Record<Accent, string> = {
  sky: "bg-sky-50 text-sky-600",
  indigo: "bg-indigo-50 text-indigo-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  muted: "bg-bg text-muted",
};

function KpiTile({
  accent,
  icon,
  label,
  value,
  meta,
}: {
  accent: Accent;
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  return (
    <Card className="relative min-w-0 overflow-hidden transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ACCENT_BAR[accent]}`} />
      <CardBody className="px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
            <p className="min-w-0 truncate">{label}</p>
            <span
              aria-hidden
              className={`grid size-7 shrink-0 place-items-center rounded-md ${ACCENT_ICON[accent]}`}
            >
              {icon}
            </span>
          </div>
          <p className="text-xl font-bold leading-tight text-ink truncate">{value}</p>
          <p className="mt-1 text-xs text-muted truncate">{meta}</p>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Table row ─────────────────────────────────────────────────────

function WarehouseTableRow({ row, onOpen }: { row: WarehouseRow; onOpen: () => void }) {
  return (
    <tr
      className="border-t border-line hover:bg-raised transition-colors cursor-pointer"
      onClick={onOpen}
    >
      {/* Name + avatar */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            aria-hidden
            className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg text-xs font-bold text-white ${avatarClassFor(row.name)}`}
          >
            {initialsFor(row.name)}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate font-semibold text-ink text-sm leading-tight">
                {row.name}
              </span>
              {row.isDefault && (
                <span className="shrink-0 inline-flex items-center px-1.5 py-px rounded border border-indigo-100 bg-indigo-50 text-indigo-700 text-xs font-semibold">
                  Default
                </span>
              )}
            </div>
            <span className="text-xs text-muted leading-none mt-0.5 block truncate">
              {row.itemCount > 0
                ? `${row.itemCount} item${row.itemCount === 1 ? "" : "s"} stocked`
                : "no items stocked"}
            </span>
          </div>
        </div>
      </td>

      {/* Code */}
      <td className="px-4 py-3">
        {row.code ? (
          <span className="font-mono text-xs font-semibold text-ink-2 bg-bg border border-line rounded px-1.5 py-0.5">
            {row.code}
          </span>
        ) : (
          <span className="text-muted text-xs">—</span>
        )}
      </td>

      {/* Type */}
      <td className="px-4 py-3">
        <TypeChip type={row.warehouseType} />
      </td>

      {/* Items stocked */}
      <td className="px-4 py-3 text-right">
        <span className="font-mono text-ink tabular-nums">{row.itemCount}</span>
      </td>

      {/* Stock value */}
      <td className="px-4 py-3 text-right">
        <span className="font-mono font-bold text-ink tabular-nums text-sm">
          {formatRupees(row.totalValuePaise)}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-3">
        <StatusPill isActive={row.isActive} />
      </td>

      {/* Arrow */}
      <td className="px-2 py-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-full text-muted group-hover:text-ink">
          <ChevronRight size={14} aria-hidden />
        </span>
      </td>
    </tr>
  );
}
